/**
 * The shape of one editorial post, everywhere it is listed — header panels, coverage
 * pages, and anything that lists WordPress articles next.
 *
 * Meta is carried in PARTS, never as a baked string: category panels read
 * "Reviews · 2d ago", editorial columns read "Dave Carr · 2d ago", and coverage pages
 * read the type eyebrow plus author. One pre-joined string would force every second
 * surface to re-parse it.
 */
/**
 * The one way a story's meta parts join into a line: "Reviews · 2d ago",
 * "Dave Carr · 2d ago". Callers pick the lead (category, author…); a missing part
 * never leaves a dangling separator.
 */
export function storyMetaLine(lead: string | null, age: string | null): string {
  return [lead, age].filter(Boolean).join(' · ')
}

export interface EditorialStory {
  /** WP post id — the join key for per-article enrichments (versus cards). `null` on
   *  cached header-nav documents built before the field existed. */
  wpPostId: number | null
  title: string
  /** This app's own route for the post (`/[slug]`), built through the shared builder. */
  url: string
  imageUrl: string | null
  /**
   * The sized ladder WP generates for the same image (WebP when it has one, down to
   * 300w) — what list `<img srcset>`s render so a 104px thumb never downloads the
   * 2560px original behind `imageUrl`. `imageUrl` stays the full-size source file:
   * og:image and the no-srcset fallback. Optional: header-nav documents cached before
   * the field existed don't carry it.
   */
  imageSrcset?: string | null
  /** WP's own standfirst — the one-line summary the coverage lists show under a title. */
  excerpt: string | null
  /** WP category display name — "Reviews", "Best products". */
  category: string | null
  author: string | null
  /**
   * The author's page on this site (`/author/[slug]`) and their WP avatar (Gravatar,
   * 96px square). The list feed has always returned `author_slug` / `author_avatar`;
   * mapped 2026-09-25 for the coverage bylines. `null`/absent on cached header-nav
   * documents built before the fields existed — consumers must tolerate `undefined`.
   */
  authorUrl?: string | null
  authorAvatarUrl?: string | null
  /** Day-granular relative age ("2d ago") — WP carries no time-of-day. */
  age: string | null
  /**
   * WP's raw date string for the post's last UPDATE (the list feed moves it when an
   * editor edits — verified live). The freshness key for per-article enrichment
   * caches (best cards): an edit rotates the key, so caches invalidate exactly when
   * content changes. `null`/absent on cached header-nav documents built before the
   * field existed.
   */
  updatedAt: string | null
  /** Review score out of 10, when the post carries one. */
  score: number | null
  /**
   * The post's award-badge image (Editor's Choice / Recommended pennant) — the list
   * feed has always returned it; surfaced 2026-08-28 for the reviews hub's thumb
   * overlay. `null`/absent on cached header-nav documents built before the field
   * existed — consumers must tolerate `undefined`, like `updatedAt`.
   */
  badgeUrl: string | null
}
